import { _ } from "../../polyglot/polyglot.js";
import { select } from "../../elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import inputFieldWidget from "../forms/fields/inputFieldWidget.js";

/**
 * `BudgetPickerWidget` is a checkbox group to manage the importing budget.
 * It allows the user to select no budget, create a new one,
 * or override an existing budget.
 */
export default class BudgetPickerWidget extends Widget {
	constructor({ budgets = [] } = {}) {
		super(...arguments);

		this._createEvents("onChange");
		this._setNoBudget();

		this._budgets = budgets;

		this._budgetNameInput = inputFieldWidget({
			name: "new-budget-name",
			klass: "form-control",
			placeHolder: _("New budget name"),
			onKeyUp: () => this.onChange.trigger(),
		});
	}

	reset() {
		this._budgets = [];
		this._setNoBudget();
	}

	setBudgets(newBudgets) {
		this._budgets = newBudgets;
		this.update();
	}

	validate({ collector }) {
		if (!this._isSelected("newBudget")) {
			return;
		}
		this._validateBudgetNameOnCreation({ collector });
	}

	getBudget() {
		return this._selection();
	}

	renderContentOn(html) {
		html.div(
			{
				klass: "form-group filter-data-to",
				"data-test": "budget-picker",
			},
			html.label({ klass: "control-label col-sm-4" }, _("Budget")),
			html.div({ klass: "col-sm-8 check-group" }, (html) =>
				this._renderOptionsOn(html),
			),
		);
	}

	_renderOptionsOn(html) {
		html.render(
			(html) => this._renderNoBudgetOn(html),
			(html) => this._renderCreateBudgetOn(html),
			(html) => this._renderReplaceBudgetOn(html),
		);
	}

	_renderNoBudgetOn(html) {
		html.div(
			this._labelRenderer({
				label: _("Do not import budget"),
				value: "noBudget",
				onChange: this._setNoBudget,
				dataTestName: "no-budget",
			}),
		);
	}

	_renderCreateBudgetOn(html) {
		html.div(
			this._labelRenderer({
				label: _("Create budget"),
				value: "newBudget",
				onChange: this._setCreateBudget,
				dataTestName: "new-budget",
			}),

			this._isSelected("newBudget") ? this._budgetNameInput : "",
		);
	}

	_renderReplaceBudgetOn(html) {
		if (!this._hasBudget()) {
			return;
		}

		this._selectedBudget ??= this._budgets[0];

		html.div(
			this._labelRenderer({
				label: _("Replace existing budget"),
				value: "replaceBudget",
				onChange: this._setReplaceBudget,
				dataTestName: "replace-budget",
			}),

			this._isSelected("replaceBudget") ? this._makeBudgetDropdown() : "",
		);
	}

	_labelRenderer({ label, value, onChange = () => {}, dataTestName = "" }) {
		const options = {
			klass: "form-control",
			type: "radio",
			name: value,
			change: onChange.bind(this),
			value,
			"data-test": dataTestName,
		};

		if (this._isSelected(value)) {
			options.checked = "checked";
		}

		return (html) => html.label(html.input(options), label);
	}

	_makeBudgetDropdown() {
		return select({
			items: this._budgets.map((budget) => ({
				label: budget.getName(),
				value: budget,
			})),
			selected: this._selectedBudget,
			onChange: (value) => (this._selectedBudget = value),
		});
	}

	_hasBudget() {
		return this._budgets.length;
	}

	_isSelected(type) {
		return this._selection().budgetType === type;
	}

	_setNoBudget() {
		this._setSelection(() => ({ budgetType: "noBudget" }));
	}

	_setCreateBudget() {
		this._setSelection(() => ({
			budgetType: "newBudget",
			budgetName: this._budgetNameInput.getValue(),
		}));
	}

	_setReplaceBudget() {
		this._setSelection(() => ({
			budgetType: "replaceBudget",
			budgetName: this._selectedBudget.getName(),
			budgetId: this._selectedBudget.getId(),
		}));
	}

	_setSelection(selection) {
		this._selection = selection;
		this.onChange.trigger();
		this.update();
	}

	_validateBudgetNameOnCreation({ collector }) {
		const name = this._budgetNameInput.getValue();
		collector.addErrorIf(!name, _("Budget name can not be empty"));

		collector.addErrorIf(
			this._budgets.some((budget) => budget.name === name),
			_("Budget name is already used"),
		);
	}
}
